import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney, formatNumber } from '../ui/format';
import { Badge, EmptyState, ErrorState, Loading, Panel, type BadgeTone } from '../ui/Page';
import { Menu } from '../ui/Menu';
import { useAuth } from '../auth/auth-store';
import { setTaskStatus } from '../work/api';
import {
  ChevronGlyph,
  GoGlyph,
  MailGlyph,
  PeopleGlyph,
  PlusGlyph,
  RangeGlyph,
  TargetGlyph,
  TuneGlyph,
  markFor,
} from './CardIcons';
import {
  getCardCatalogue,
  getDashboard,
  getLayout,
  resetLayout,
  saveLayout,
  type CardResult,
  type CardSize,
  type CatalogueEntry,
  type Period,
  type PlacedCard,
} from './api';

/**
 * The home screen (context.md §34, §133).
 *
 * Which figures matter is a question about the business, so the cards are a stored choice rather
 * than a fixed screen. An administrator arranges the company's; anybody may arrange their own on top
 * of it, and dropping theirs puts them back on the company's.
 *
 * What the arrangement decides is not only order but **weight**. A figure at size `M` is a headline
 * — a big number with its own tinted mark — and at size `S` it is one reading on the strip of small
 * numbers underneath. Same query, two ways of being read, and which one a number deserves is a
 * decision about the business rather than about the layout.
 *
 * Read top to bottom it is a morning: the four headlines, the strip of readings, then the two panels
 * somebody actually works from — where the money is sitting, and what they have promised to do —
 * and last the three that are only read. Only cards somebody may see are returned by the API, so
 * nothing here renders an empty box because of a permission.
 */

const PERIODS: Period[] = ['TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'THIS_MONTH', 'THIS_YEAR'];

/** Where the "Criar" menu goes. Each one opens the create affordance that screen already has. */
const CREATE: { key: string; to: string }[] = [
  { key: 'lead', to: '/crm/leads?criar=1' },
  { key: 'deal', to: '/funil?criar=1' },
  { key: 'contact', to: '/crm/contatos?criar=1' },
  { key: 'company', to: '/crm/empresas?criar=1' },
  { key: 'task', to: '/trabalho/tarefas?criar=1' },
];

export function DashboardPage(): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>('LAST_30_DAYS');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dashboard = useQuery({
    queryKey: ['dashboard', period],
    queryFn: () => getDashboard(period),
  });

  const cards = dashboard.data?.cards ?? [];
  const headlines = cards.filter((card) => card.shape === 'FIGURE' && card.size !== 'S');
  const strip = cards.filter((card) => card.shape === 'FIGURE' && card.size === 'S');
  const panels = cards.filter((card) => card.shape !== 'FIGURE');

  return (
    <div className="view view--wide home">
      <header className="home-head">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="muted home-head__greeting">
            <span className="strong">
              {t('dashboard.greeting').replace('{name}', firstName(user?.name))}
            </span>{' '}
            {t('dashboard.description')}
          </p>
        </div>

        <div className="home-head__actions">
          {/* The window and the period are one control: the dates are what the cards answered
              about, and the name is which rule produced them. */}
          <Menu
            label={t('dashboard.period')}
            className="range-picker"
            trigger={
              <>
                <RangeGlyph />
                <span>
                  {dashboard.data
                    ? `${formatDate(dashboard.data.from)} – ${formatDate(dashboard.data.to)}`
                    : t('dashboard.period')}
                </span>
                <ChevronGlyph />
              </>
            }
          >
            {PERIODS.map((value) => (
              <button
                key={value}
                type="button"
                aria-current={period === value ? 'true' : undefined}
                onClick={() => setPeriod(value)}
              >
                {t(`dashboard.period.${value}`)}
              </button>
            ))}
          </Menu>

          <span className="range-tag">{t(`dashboard.period.${period}`)}</span>

          <button
            type="button"
            className="button button--quiet"
            aria-pressed={editing}
            onClick={() => setEditing((current) => !current)}
          >
            <TuneGlyph />
            {editing ? t('dashboard.done') : t('dashboard.arrange')}
          </button>

          <Menu
            label={t('dashboard.create')}
            className="button button--primary home-create"
            trigger={
              <>
                <PlusGlyph />
                {t('dashboard.create')}
                <ChevronGlyph />
              </>
            }
          >
            {CREATE.map((entry) => (
              <Link key={entry.key} to={entry.to} role="menuitem">
                {t(`dashboard.create.${entry.key}`)}
              </Link>
            ))}
          </Menu>
        </div>
      </header>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {editing && (
        <LayoutEditor
          onSaved={() => {
            setEditing(false);
            setError(null);
            void client.invalidateQueries({ queryKey: ['dashboard'] });
          }}
          onError={setError}
        />
      )}

      {dashboard.error ? (
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : dashboard.isLoading ? (
        <Loading />
      ) : cards.length === 0 ? (
        <EmptyState title={t('dashboard.empty')} description={t('dashboard.emptyBody')} />
      ) : (
        <>
          {headlines.length > 0 && (
            <div className="home-kpis">
              {headlines.map((card) => (
                <Headline key={card.card} card={card} />
              ))}
            </div>
          )}

          {strip.length > 0 && (
            <div className="home-strip">
              {strip.map((card) => (
                <Reading key={card.card} card={card} />
              ))}
            </div>
          )}

          <div className="home-panels">
            {panels.map((card) => (
              <DashPanel key={card.card} card={card} />
            ))}
          </div>
        </>
      )}

      {dashboard.data?.scope === 'PERSONAL' && (
        <p className="muted small">{t('dashboard.personalNote')}</p>
      )}
    </div>
  );
}

/**
 * A headline figure: the mark, the label, the number, and one line saying what is behind it.
 *
 * The delta is against the previous period of the same length, shown only when the card follows the
 * period and there is a base to compare with — "+∞%" against zero is not information.
 */
function Headline({ card }: { card: CardResult }): React.JSX.Element {
  const figure = card.figure!;
  const mark = markFor(card.card);
  const delta = figure.delta ?? null;
  const showDelta =
    delta !== null && figure.previous !== undefined && !(figure.value === 0 && figure.previous === 0);

  return (
    <article className="home-kpi">
      <span className={`metric__icon metric__icon--${mark.tone}`} aria-hidden="true">
        {mark.icon}
      </span>

      <span className="cell-stack">
        <span className="home-kpi__label">{t(`dashboard.card.${card.card}`)}</span>
        <span className="home-kpi__value">{format(figure.value, figure.unit)}</span>
        <span className="home-kpi__foot">
          <Caption card={card} />
          {showDelta && (
            <span
              className={`kpi__delta${goodness(card, delta) ? ' kpi__delta--up' : ' kpi__delta--down'}`}
              title={t('dashboard.previous').replace('{v}', format(figure.previous!, figure.unit))}
            >
              {delta! > 0 ? '▲' : delta! < 0 ? '▼' : '•'} {Math.abs(Math.round(delta! * 100))}%
            </span>
          )}
        </span>
      </span>
    </article>
  );
}

/** One reading on the strip: small, and read in passing. */
function Reading({ card }: { card: CardResult }): React.JSX.Element {
  const figure = card.figure!;
  const mark = markFor(card.card);
  const alarming = lowerIsBetter(card.card) && figure.value > 0;

  return (
    <div className="home-reading">
      <span className={`home-reading__icon home-reading__icon--${mark.tone}`} aria-hidden="true">
        {mark.icon}
      </span>

      <span className="cell-stack">
        <span className="home-reading__label">{t(`dashboard.card.${card.card}`)}</span>
        <span className={`home-reading__value${alarming ? ' home-reading__value--alarm' : ''}`}>
          {format(figure.value, figure.unit)}
        </span>
      </span>
    </div>
  );
}

/** The line under a headline: what the figure is made of, in the dictionary's words. */
function Caption({ card }: { card: CardResult }): React.JSX.Element | null {
  const figure = card.figure!;
  const key = `dashboard.secondary.${card.card}`;
  const caption = t(key);

  if (caption === key || figure.secondary === undefined) return null;

  return (
    <span className="muted small">
      {caption
        .replace('{n}', formatNumber(figure.secondary))
        .replace('{p}', String(figure.ratio ?? 0))}
    </span>
  );
}

/** What each panel wears in its header, beyond its name. */
const CHROME: Record<
  string,
  { subtitle?: string; icon?: React.JSX.Element; to?: string; action?: string }
> = {
  'sales.by_stage': { subtitle: 'dashboard.sub.by_stage', to: '/funil', action: 'dashboard.seeBoard' },
  'work.my_tasks': { to: '/trabalho/tarefas?criar=1', action: 'dashboard.newTask' },
  'crm.customers_by_status': { icon: <PeopleGlyph /> },
  'crm.customers_by_source': { icon: <TargetGlyph /> },
  'marketing.campaign_results': {
    icon: <MailGlyph />,
    to: '/marketing/campanhas',
    action: 'dashboard.seeAll',
  },
};

function DashPanel({ card }: { card: CardResult }): React.JSX.Element {
  const chrome = CHROME[card.card] ?? {};
  const counted = card.card === 'work.my_tasks' ? (card.figure?.value ?? 0) : null;

  return (
    <section className={`panel home-panel home-panel--${card.size.toLowerCase()}`}>
      <header className="home-panel__head">
        <span className="home-panel__title">
          {chrome.icon}
          <span className="cell-stack">
            <span className="home-panel__name">
              <h2>{t(`dashboard.card.${card.card}`)}</h2>
              {counted !== null && <span className="home-panel__count">{counted}</span>}
            </span>
            {chrome.subtitle && <span className="muted small">{t(chrome.subtitle)}</span>}
          </span>
        </span>

        {chrome.to && chrome.action && (
          <Link to={chrome.to} className="home-panel__action">
            {card.card === 'work.my_tasks' && <PlusGlyph />}
            {t(chrome.action)}
            {card.card !== 'work.my_tasks' && <GoGlyph />}
          </Link>
        )}
      </header>

      <div className="home-panel__body">
        {card.points && <Series points={card.points} unit={card.unit} />}
        {card.slices && <Breakdown card={card} />}
        {card.rows && <Rows card={card} />}
      </div>
    </section>
  );
}

/**
 * A series as an area chart drawn by hand.
 *
 * Inline SVG, no library: a filled area, a line, the first and last day as labels, and a tooltip
 * per day. Enough to see a trend and a spike, which is what a dashboard chart is for.
 */
function Series({
  points,
  unit,
}: {
  points: { day: string; value: number }[];
  unit?: string;
}): React.JSX.Element {
  if (points.length === 0 || points.every((p) => p.value === 0)) {
    return <p className="muted small">{t('dashboard.noData')}</p>;
  }

  const width = 600;
  const height = 160;
  const pad = { top: 12, right: 8, bottom: 22, left: 8 };
  const max = Math.max(...points.map((p) => p.value), 1);
  const stepX = (width - pad.left - pad.right) / Math.max(points.length - 1, 1);
  const x = (i: number): number => pad.left + i * stepX;
  const y = (v: number): number => pad.top + (height - pad.top - pad.bottom) * (1 - v / max);

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${(height - pad.bottom).toFixed(1)} L${x(0).toFixed(1)},${(height - pad.bottom).toFixed(1)} Z`;
  const peak = points.reduce((best, p, i) => (p.value > points[best]!.value ? i : best), 0);

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart__svg"
        role="img"
        aria-label={t('dashboard.seriesLabel')}
      >
        <defs>
          <linearGradient id="chart-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={pad.left}
            x2={width - pad.right}
            y1={y(max * f)}
            y2={y(max * f)}
            className="chart__grid"
          />
        ))}
        <path d={area} fill="url(#chart-fill)" />
        <path d={line} className="chart__line" />
        {points.map((p, i) => (
          <g key={p.day}>
            <circle cx={x(i)} cy={y(p.value)} r={i === peak ? 4 : 2.5} className="chart__dot" />
            <rect
              x={x(i) - stepX / 2}
              y={pad.top}
              width={stepX}
              height={height - pad.top - pad.bottom}
              fill="transparent"
            >
              <title>{`${formatDate(p.day)} · ${format(p.value, unit)}`}</title>
            </rect>
          </g>
        ))}
        <text x={pad.left} y={height - 6} className="chart__axis">
          {formatDate(points[0]!.day)}
        </text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" className="chart__axis">
          {formatDate(points.at(-1)!.day)}
        </text>
        <text
          x={x(peak)}
          y={y(points[peak]!.value) - 8}
          textAnchor="middle"
          className="chart__peak"
        >
          {format(points[peak]!.value, unit)}
        </text>
      </svg>
    </div>
  );
}

/**
 * A breakdown as a list of readings rather than a row of bars.
 *
 * "5 novos, 0 contatados" is a queue, not a proportion, and a bar invites exactly the wrong
 * comparison: a bar answers "what share", which is rarely the question asked of six small numbers.
 * The dot carries the tenant's own colour for that entry, so the same status is the same colour
 * everywhere in the product, and the word beside it carries the meaning (WCAG 2.2 AA, §127).
 */
function Breakdown({ card }: { card: CardResult }): React.JSX.Element {
  const slices = card.slices!;
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const emphatic = card.card === 'sales.by_stage';
  const footKey = `dashboard.foot.${card.card}`;
  const foot = t(footKey);

  if (slices.length === 0 || total === 0) {
    return <p className="muted small">{t('dashboard.noData')}</p>;
  }

  return (
    <>
      {emphatic && (
        <div className="home-breakdown__head">
          <span>{t('dashboard.stage')}</span>
          <span>{t('dashboard.value')}</span>
        </div>
      )}

      <ul className="home-breakdown">
        {slices.map((slice) => (
          <li key={slice.label}>
            <span className="home-breakdown__label">
              <span
                className="dot"
                style={{ '--dot-color': slice.color ?? 'var(--color-accent)' } as React.CSSProperties}
                aria-hidden="true"
              />
              {slice.label}
            </span>
            <span className="home-breakdown__value">{format(slice.value, card.unit)}</span>
          </li>
        ))}
      </ul>

      {emphatic ? (
        <div className="home-total">
          <span className="cell-stack">
            <span className="strong">{t('dashboard.pipelineTotal')}</span>
            {card.figure?.secondary !== undefined && (
              <span className="muted small">
                {t('dashboard.secondary.sales.open_pipeline').replace(
                  '{n}',
                  formatNumber(card.figure.secondary),
                )}
              </span>
            )}
          </span>
          <strong className="home-total__value">
            {format(card.figure?.value ?? total, card.unit)}
          </strong>
        </div>
      ) : (
        foot !== footKey && (
          <p className="home-panel__foot muted small">
            {foot.replace('{n}', formatNumber(total))}
          </p>
        )
      )}
    </>
  );
}

function toneOf(tone: string | undefined, badge: string | null): BadgeTone {
  if (tone === 'danger' || tone === 'warning' || tone === 'positive' || tone === 'info') return tone;
  if (badge === 'HIGH' || badge === 'URGENT') return 'danger';
  if (badge === 'MEDIUM') return 'warning';

  return 'neutral';
}

function Rows({ card }: { card: CardResult }): React.JSX.Element {
  if (card.card === 'work.my_tasks') return <TaskRows card={card} />;
  if (card.card === 'marketing.campaign_results') return <CampaignRows card={card} />;

  return <AttentionRows card={card} />;
}

/**
 * The tasks somebody has open, and the tick that finishes one.
 *
 * The box is a real control, not a decoration: a checkbox that does nothing is worse than no
 * checkbox at all. The task's version travels with the row so the tick can finish it without
 * reading it first, and a stale version is refused rather than overwriting somebody else's change.
 */
function TaskRows({ card }: { card: CardResult }): React.JSX.Element {
  const client = useQueryClient();
  const rows = card.rows!;
  const [error, setError] = useState<string | null>(null);

  const finish = useMutation({
    mutationFn: (row: { id: string; version: number }) =>
      setTaskStatus(row.id, { status: 'DONE', expectedVersion: row.version }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['dashboard'] });
      void client.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /*
   * Midnight this morning, here.
   *
   * Lateness is a question about the reader's own day, so it is answered in the browser rather than
   * in a server process that thinks in UTC — a task due this evening in São Paulo was arriving
   * already marked late, or not, depending on the hour the card was drawn. Anything falling before
   * today began is late; anything due later today is not.
   */
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const isLate = (iso: string | null): boolean =>
    iso !== null && new Date(iso).getTime() < startOfToday.getTime();

  const overdue = rows.filter((row) =>
    isLate(typeof row.data?.dueDate === 'string' ? row.data.dueDate : null),
  ).length;
  const total = card.figure?.value ?? rows.length;

  if (rows.length === 0) {
    return <p className="muted small">{t('dashboard.noTasks')}</p>;
  }

  return (
    <>
      {overdue > 0 && (
        <p className="home-alarm">
          <span className="cell-inline">
            <span className="dot" style={{ '--dot-color': 'var(--color-down)' } as React.CSSProperties} aria-hidden="true" />
            {t('dashboard.overdueTasks').replace('{n}', String(overdue))}
          </span>

          <Link to="/trabalho/tarefas?filter=overdue" className="home-alarm__action">
            {t('dashboard.seeOverdue')}
            <GoGlyph />
          </Link>
        </p>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="table-scroll">
        <table className="table home-tasks">
          <thead>
            <tr>
              <th className="home-tasks__tick" aria-label={t('dashboard.finish')} />
              <th>{t('dashboard.task')}</th>
              <th>{t('dashboard.due')}</th>
              <th>{t('dashboard.priority')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const due = typeof row.data?.dueDate === 'string' ? row.data.dueDate : null;
              const version = typeof row.data?.version === 'number' ? row.data.version : null;
              const late = isLate(due);

              return (
                <tr key={row.id}>
                  <td className="home-tasks__tick">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={version === null || finish.isPending}
                      aria-label={t('dashboard.finishTask').replace('{title}', row.title)}
                      onChange={() => version !== null && finish.mutate({ id: row.id, version })}
                    />
                  </td>
                  <td>
                    <Link to={row.href ?? '/trabalho/tarefas'}>{row.title}</Link>
                  </td>
                  <td className={late ? 'cell-late' : ''}>{due ? formatDate(due) : '—'}</td>
                  <td>
                    {row.badge && (
                      <Badge tone={toneOf(undefined, row.badge)}>
                        {t(`work.priority.${row.badge}`)}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="home-panel__foot">
        <span className="muted small">
          {t('dashboard.showing')
            .replace('{n}', String(rows.length))
            .replace('{m}', String(total))}
        </span>

        <Link to="/trabalho/tarefas" className="home-panel__action">
          {t('dashboard.seeAll')}
          <GoGlyph />
        </Link>
      </p>
    </>
  );
}

/** What went out, and how it landed. */
function CampaignRows({ card }: { card: CardResult }): React.JSX.Element {
  const rows = card.rows!;

  if (rows.length === 0) return <p className="muted small">{t('dashboard.noCampaigns')}</p>;

  return (
    <ul className="home-rows">
      {rows.map((row) => (
        <li key={row.id}>
          <span className="cell-stack">
            <span className="strong">{row.title}</span>
            <span className="muted small">
              {t('dashboard.campaignLine')
                .replace('{sent}', formatNumber(Number(row.data?.sent ?? 0)))
                .replace('{opened}', formatNumber(Number(row.data?.opened ?? 0)))}
            </span>
          </span>

          {row.badge && (
            <Badge tone={row.badge === 'SENT' ? 'positive' : row.badge === 'FAILED' ? 'danger' : 'neutral'}>
              {t(`campaigns.state.${row.badge}`)}
            </Badge>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Everything that is waiting on somebody, as counts that go somewhere. */
function AttentionRows({ card }: { card: CardResult }): React.JSX.Element {
  const rows = card.rows!;

  if (rows.length === 0) return <p className="muted small">{t('dashboard.allClear')}</p>;

  return (
    <ul className="home-rows">
      {rows.map((row) => {
        const body = (
          <>
            <span className="cell-stack">
              <span className="strong">
                {card.card === 'work.attention' ? t(`dashboard.attention.${row.title}`) : row.title}
              </span>
              {row.detail && <span className="muted small">{row.detail}</span>}
            </span>
            {row.badge && <Badge tone={toneOf(row.tone, row.badge)}>{row.badge}</Badge>}
          </>
        );

        return (
          <li key={row.id}>
            {row.href ? (
              <Link to={row.href} className="home-rows__link">
                {body}
              </Link>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}

function format(value: number, unit?: string): string {
  if (unit === 'CENTS') return formatMoney(value);
  if (unit === 'PERCENT') return `${value}%`;

  return formatNumber(value);
}

/** Two figures where a rise is bad news; everywhere else up is good. */
function lowerIsBetter(key: string): boolean {
  return key === 'work.overdue_tasks' || key === 'inbox.unassigned';
}

function goodness(card: CardResult, delta: number | null): boolean {
  if (delta === null) return true;

  return lowerIsBetter(card.card) ? delta <= 0 : delta >= 0;
}

/** The name somebody is greeted by. A full name in a greeting reads like a form letter. */
function firstName(name: string | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] || '';
}

/**
 * Choosing the cards.
 *
 * Two lists rather than drag and drop: what is on the dashboard, in order, and what else could be.
 * Reordering is a pair of arrows, which is slower than dragging and works on a phone, with a
 * keyboard, and for somebody who cannot hold a mouse button down.
 */
function LayoutEditor({
  onSaved,
  onError,
}: {
  onSaved: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [cards, setCards] = useState<PlacedCard[] | null>(null);
  const [scope, setScope] = useState<'TENANT' | 'PERSONAL'>('PERSONAL');

  const layout = useQuery({ queryKey: ['dashboard-layout'], queryFn: getLayout });
  const catalogue = useQuery({ queryKey: ['dashboard-cards'], queryFn: getCardCatalogue });

  useEffect(() => {
    if (layout.data && cards === null) setCards(layout.data.cards);
  }, [layout.data, cards]);

  const save = useMutation({
    mutationFn: () => saveLayout({ scope, cards: cards ?? [] }),
    onSuccess: onSaved,
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const reset = useMutation({
    mutationFn: (scope: 'TENANT' | 'PERSONAL') => resetLayout(scope),
    onSuccess: (data) => {
      setCards(data.cards);
      onSaved();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  if (layout.isLoading || catalogue.isLoading || cards === null) return <Loading />;

  const chosen = new Set(cards.map((card) => card.card));
  const available = (catalogue.data?.cards ?? []).filter((entry) => !chosen.has(entry.key));

  const move = (index: number, by: number): void => {
    const next = [...cards];
    const target = index + by;

    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target]!, next[index]!];
    setCards(next);
  };

  const resize = (index: number, size: CardSize): void => {
    setCards(cards.map((card, position) => (position === index ? { ...card, size } : card)));
  };

  const sizesOf = (key: string): CardSize[] =>
    catalogue.data?.cards.find((entry) => entry.key === key)?.sizes ?? ['S'];

  return (
    <Panel title={t('dashboard.arranging')}>
      <p className="muted small">{t('dashboard.arrangingHint')}</p>

      <div className="home-grid">
        <div className="stack">
          <h4>{t('dashboard.onIt')}</h4>

          <ol className="dash__chosen">
            {cards.map((card, index) => (
              <li key={card.card}>
                <span className="cell-inline">
                  <span className="strong">{t(`dashboard.card.${card.card}`)}</span>

                  <span className="cell-inline">
                    {sizesOf(card.card).length > 1 &&
                      sizesOf(card.card).map((size) => (
                        <button
                          key={size}
                          type="button"
                          className={`chip${card.size === size ? ' chip--active' : ''}`}
                          onClick={() => resize(index, size)}
                        >
                          {t(`dashboard.size.${size}`)}
                        </button>
                      ))}

                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      aria-label={t('dashboard.moveUp')}
                      onClick={() => move(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      aria-label={t('dashboard.moveDown')}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      onClick={() => setCards(cards.filter((entry) => entry.card !== card.card))}
                    >
                      {t('dashboard.removeCard')}
                    </button>
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="stack">
          <h4>{t('dashboard.available')}</h4>

          {available.length === 0 ? (
            <p className="muted small">{t('dashboard.allUsed')}</p>
          ) : (
            <ul className="dash__available">
              {available.map((entry: CatalogueEntry) => (
                <li key={entry.key}>
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    disabled={!entry.available}
                    title={entry.available ? undefined : t('dashboard.notAvailable')}
                    onClick={() => setCards([...cards, { card: entry.key, size: entry.sizes[0]! }])}
                  >
                    + {t(`dashboard.card.${entry.key}`)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="cell-inline">
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={scope === 'TENANT'}
            onChange={(event) => setScope(event.target.checked ? 'TENANT' : 'PERSONAL')}
          />
          <span>{t('dashboard.forEveryone')}</span>
        </label>

        <button
          type="button"
          className="button button--primary"
          disabled={cards.length === 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('work.creating') : t('dashboard.save')}
        </button>

        {layout.data?.scope === 'PERSONAL' ? (
          <button
            type="button"
            className="button button--quiet"
            disabled={reset.isPending}
            onClick={() => reset.mutate('PERSONAL')}
          >
            {t('dashboard.useCompany')}
          </button>
        ) : (
          /* Without this a saved company arrangement is a one-way door: a later release's screen
             would be unreachable for everybody in the tenant, forever. */
          <button
            type="button"
            className="button button--quiet"
            disabled={reset.isPending}
            title={t('dashboard.useDefaultHint')}
            onClick={() => reset.mutate('TENANT')}
          >
            {t('dashboard.useDefault')}
          </button>
        )}
      </div>
    </Panel>
  );
}
